import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { WORDPROCESSINGML_NS } from "@common/utils/redline/ooxml/ooxml-names";
import { ParagraphModel } from "@common/utils/redline/ooxml/paragraph-model";
import { Revision } from "@common/utils/redline/ooxml/revision";
import { ParagraphSplicer } from "@common/utils/redline/paragraph-splicer";
import type { AcceptedTextSpan } from "@common/utils/redline/redline-engine";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import { textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";

import { AnchoredProposal, type ChangeComposedOverTable } from "../../../utils/anchor/proposal/anchored-proposal";
import type { ParagraphGroupMember, TrackerParagraphGroup } from "./paragraph-groups";
import { textRedlineOfTrackerCard } from "./tracker-types";
import { CHANGE_COLORS } from "./word-like-paragraph";

export const tableGroupAuthorOf = (cardNumber: number): string => `#${cardNumber}`;

export interface CommentedCellMark {
  commentId: number;
  anchorText: string | null;
}

export function tableCardColorByAuthorOf(group: TrackerParagraphGroup): Map<string, string> {
  const cardNumbersInOrder = [...new Set(group.members.map((member) => member.action.cardNumber))].sort((left, right) => left - right);
  return new Map(cardNumbersInOrder.map((cardNumber, index) => [tableGroupAuthorOf(cardNumber), CHANGE_COLORS[index % CHANGE_COLORS.length]]));
}

export function tableChangeColorsOf(group: TrackerParagraphGroup): Map<string, string> {
  const cardColorByAuthor = tableCardColorByAuthorOf(group);
  return new Map(group.members.map((member) => [member.action.id, cardColorByAuthor.get(tableGroupAuthorOf(member.action.cardNumber)) ?? CHANGE_COLORS[0]]));
}

export interface TableGroupChange extends ChangeComposedOverTable {
  actionId: string;
}

export function tableGroupChangesOf(members: ParagraphGroupMember[], stampedAt: string): TableGroupChange[] {
  return members.flatMap((member, position) => {
    const textRedline = textRedlineOfTrackerCard(member.card);
    if (textRedline === null) return [];
    const proposal = new AnchoredProposal(textsPayloadFrom({ ...textRedline, author: tableGroupAuthorOf(member.action.cardNumber) }), "asTrackedChange");
    return [{ proposal, stampedAt: new Date(Date.parse(stampedAt) + position).toISOString(), actionId: member.action.id }];
  });
}

function anchorSpanOf(acceptedText: string, anchorText: string): AcceptedTextSpan | null {
  const units = WordTokenizer.diffUnits(acceptedText);
  const unitKeys = units.map((unit) => WordTokenizer.comparisonKey(unit.text));
  const anchorKeys = WordTokenizer.diffUnits(anchorText).map((unit) => WordTokenizer.comparisonKey(unit.text));
  if (anchorKeys.length === 0) return null;
  for (let start = 0; start + anchorKeys.length <= units.length; start += 1) {
    if (anchorKeys.every((anchorKey, offset) => unitKeys[start + offset] === anchorKey)) {
      return { startOffset: units[start].startOffset, endOffset: units[start + anchorKeys.length - 1].endOffset };
    }
  }
  return null;
}

function anchoredRunsOf(paragraph: Element, mark: CommentedCellMark, stampedAt: string): Element[] {
  if (mark.anchorText === null) return [];
  const model = new ParagraphModel(paragraph);
  const span = anchorSpanOf(model.text, mark.anchorText);
  if (span === null) return [];
  const splicer = new ParagraphSplicer(model, new Revision({ author: tableGroupAuthorOf(mark.commentId), stampedAt, firstAllocatedRevisionId: 1 }), "vacate");
  return splicer.isolateAcceptedCharacterRange(span.startOffset, span.endOffset);
}

function commentRangeMarkOf(paragraph: Element, markName: "w:commentRangeStart" | "w:commentRangeEnd", commentId: number): Element {
  const mark = paragraph.ownerDocument.createElementNS(WORDPROCESSINGML_NS, markName);
  mark.setAttributeNS(WORDPROCESSINGML_NS, "w:id", String(commentId));
  return mark;
}

export function withCommentedCells(composedTable: string, marksByBookmark: ReadonlyMap<string, CommentedCellMark>, stampedAt: string): string {
  if (marksByBookmark.size === 0) return composedTable;
  const range = new OoxmlRange(composedTable);
  const paragraphByBookmark = range.paragraphElementsByBookmarkName();
  let marked = false;
  for (const [bookmark, mark] of marksByBookmark) {
    const paragraph = paragraphByBookmark.get(bookmark);
    if (paragraph === undefined) continue;
    const start = commentRangeMarkOf(paragraph, "w:commentRangeStart", mark.commentId);
    const end = commentRangeMarkOf(paragraph, "w:commentRangeEnd", mark.commentId);
    const anchoredRuns = anchoredRunsOf(paragraph, mark, stampedAt);
    if (anchoredRuns.length > 0) {
      const firstRun = anchoredRuns[0];
      const lastRun = anchoredRuns[anchoredRuns.length - 1];
      firstRun.parentNode?.insertBefore(start, firstRun);
      lastRun.parentNode?.insertBefore(end, lastRun.nextSibling);
    } else {
      const properties = Array.from(paragraph.childNodes).find((node): node is Element => node.nodeType === 1 && (node as Element).localName === "pPr") ?? null;
      paragraph.insertBefore(start, properties === null ? paragraph.firstChild : properties.nextSibling);
      paragraph.appendChild(end);
    }
    marked = true;
  }
  return marked ? range.serialize() : composedTable;
}
